import {IEventService} from "../../../services/eventService";
import {DateObservable} from "../../observables/dateObservable";
import {action, computed, makeObservable, observable, runInAction} from "mobx";
import {KeyValue} from "../../observables/keyValue";
import {add} from "date-fns";
import {AuthUser} from "../../../utils/auth/user";
import {EventMetricsSummary} from "./models/eventMetricsSummary";
import {VillageMap} from "../../../shared/multiVillageHelper";
import {Village} from "../../../shared/interfaces/village";
import {
  summariseEventsIntoMetrics,
  summariseNoticesIntoMetrics
} from "../../../utils/metrics/metricsUtils";
import {
  EngageMetricsTypes,
  EnhancedMetricsTypes,
  EventMetricsColumn,
  EventsMetricsRow
} from "../../../utils/metrics/eventMetrics";
import {VillageCalendar} from "../../../shared/interfaces/villageCalendar";
import {isDateOneAfterDateTwo} from "../../../utils/dateUtils";
import {INoticeService} from "../../../services/noticeService";
import {NoticeMetricsSummary} from "./models/noticeMetricsSummary";
import {
  NoticeMetricsColumn,
  NoticeMetricsRow,
  NoticeMetricsTypes
} from "../../../utils/metrics/noticeMetrics";
import {
  CalendarTypeOptions,
  MetricsColumn,
  MetricsRow,
  MetricsTabOption
} from "../../../utils/metrics/metricTypes";
import {IVillageCalendarsDomainStore} from "./villageCalendarsDomainStore";

export interface IMetricsDomainStore {
  computedMetrics: MetricsRow[];

  startDate: DateObservable;
  endDate: DateObservable;

  selectedVillages: KeyValue[];
  setSelectedVillages: (villages: KeyValue[]) => void;

  selectedTab: MetricsTabOption;
  setSelectedTab: (tab: MetricsTabOption) => void;

  selectedCalendarType: CalendarTypeOptions;
  setSelectedCalendarType: (
    calendarType: CalendarTypeOptions | null | undefined
  ) => void;

  getVillageMap: () => VillageMap;
  getVillages: () => Village[] | null;

  loadData: () => Promise<void>;

  canExport: boolean;
  requiresReload: boolean;

  metricsColumns: MetricsColumn<any, any>[];

  searchParams?: {
    selectedVillages: KeyValue[];
    selectedCalendarType?: CalendarTypeOptions;
    startDate: Date;
    endDate: Date;
  };

  isSearchValid: boolean;
  isDirty: boolean;
}

export class MetricsDomainStore implements IMetricsDomainStore {
  private readonly eventService: IEventService;
  private readonly noticeService: INoticeService;
  private readonly villageCalendarsDomainStore?: IVillageCalendarsDomainStore;

  events: EventMetricsSummary[] = [];
  notices: NoticeMetricsSummary[] = [];
  startDate: DateObservable;
  endDate: DateObservable;
  selectedVillages: KeyValue[] = [];
  selectedCalendarType: CalendarTypeOptions = "Enhanced";
  selectedTab: MetricsTabOption = "Events";
  isDirty = false;

  // Search params is a cache of the search params used to load the data
  // This allows us to only update the UI once the data has loaded.
  eventSearchParams?: {
    selectedVillages: KeyValue[];
    selectedCalendarType: CalendarTypeOptions;
    startDate: Date;
    endDate: Date;
  };

  noticeSearchParams?: {
    selectedVillages: KeyValue[];
    startDate: Date;
    endDate: Date;
  };

  constructor(
    eventService: IEventService,
    noticeService: INoticeService,
    villageCalendarsDomainStore?: IVillageCalendarsDomainStore
  ) {
    this.eventService = eventService;
    this.noticeService = noticeService;
    this.villageCalendarsDomainStore = villageCalendarsDomainStore;

    const sd = add(new Date(), {weeks: -1});
    const ed = new Date();

    this.startDate = new DateObservable(
      new Date(sd.getFullYear(), sd.getMonth(), sd.getDate(), 0, 0, 0, 0),
      null,
      (startDate) => {
        return Boolean(
          this.endDate.date && startDate && startDate < this.endDate.date
        );
      }
    );
    this.endDate = new DateObservable(
      new Date(ed.getFullYear(), ed.getMonth(), ed.getDate(), 0, 0, 0, 0),
      null,
      (endDate) => {
        return Boolean(
          this.startDate.date && endDate && this.startDate.date < endDate
        );
      }
    );

    makeObservable(this, {
      events: observable,
      notices: observable,
      startDate: observable,
      endDate: observable,
      selectedVillages: observable,
      selectedCalendarType: observable,
      selectedTab: observable,
      isDirty: observable,
      eventSearchParams: observable,
      noticeSearchParams: observable,

      searchParams: computed,
      metricsColumns: computed,
      requiresReload: computed,
      canExport: computed,
      computedMetrics: computed,
      computedEventMetrics: computed,
      eventMetricsColumns: computed,
      computedNoticeMetrics: computed,
      noticeMetricsColumns: computed,
      isSearchValid: computed,

      loadData: action,
      loadEvents: action,
      loadNotices: action,
      setSelectedTab: action,
      setSelectedVillages: action,
      setSelectedCalendarType: action
    });
  }

  get searchParams() {
    return this.selectedTab === "Events"
      ? this.eventSearchParams
      : this.noticeSearchParams;
  }

  get metricsColumns(): MetricsColumn<any, any>[] {
    return this.selectedTab === "Events"
      ? this.eventMetricsColumns
      : this.noticeMetricsColumns;
  }

  loadData = () => {
    if (this.selectedTab === "Events") {
      return this.loadEvents();
    } else {
      return this.loadNotices();
    }
  };

  loadEvents = async () => {
    if (!this.startDate.date || !this.endDate.date) return;

    this.isDirty = true;

    if (!this.isSearchValid) return;

    // Cache search params before any await, so that edits made while loading
    // can't bypass the validation above or change what the results are for.
    // We can't set them yet, otherwise the UI will change before the data is loaded.
    const searchParams = {
      selectedVillages: [...this.selectedVillages],
      selectedCalendarType: this.selectedCalendarType,
      startDate: this.startDate.date,
      endDate: this.endDate.date
    };

    if (this.villageCalendarsDomainStore) {
      const cmsCalendars = this.villageCalendarsDomainStore.villageCalendars;
      // personTypeIds == null on every calendar means the previous load's CMS
      // enrichment failed (the BFF omits the field on failure), so retry
      // rather than trust the cached list. Events may also have populated the
      // shared cache with only scoped (per-village) loads, so require the
      // all-villages load before reusing it.
      if (
        !this.villageCalendarsDomainStore.hasAllVillageCalendarsLoaded ||
        !cmsCalendars ||
        cmsCalendars.length === 0 ||
        cmsCalendars.every((c) => c.personTypeIds == null)
      ) {
        try {
          await this.villageCalendarsDomainStore.getVillageCalendars();
        } catch {
          // Non-fatal: fall back to the Integration API values below.
        }
      }
    }

    const selectedVillages = searchParams.selectedVillages.map((v) => v.value);

    const data = await this.eventService.getAllEvents(
      searchParams.startDate,
      searchParams.endDate,
      selectedVillages.join(","),
      this.getSelectedPersonTypeIds(
        searchParams.selectedVillages,
        searchParams.selectedCalendarType
      ),
      true
    );

    runInAction(() => {
      this.events = data.map((e: any) => new EventMetricsSummary(e));
      this.eventSearchParams = searchParams;
    });
  };

  loadNotices = async () => {
    if (!this.startDate.date || !this.endDate.date) return;

    this.isDirty = true;

    if (!this.isSearchValid) return;

    const selectedVillages = this.selectedVillages.map((v) => v.value);

    // Cache search params now, so that if the user edits while loading, we still know what the results are for.
    // We can't set them yet, otherwise the UI will change before the data is loaded.
    const searchParams = {
      selectedVillages: this.selectedVillages,
      startDate: this.startDate.date,
      endDate: this.endDate.date
    };

    const data = await this.noticeService.getNoticesForVillages(
      selectedVillages,
      this.startDate.date,
      this.endDate.date
    );

    runInAction(() => {
      this.notices = data.notices.map((n: any) => new NoticeMetricsSummary(n));
      this.noticeSearchParams = searchParams;
    });
  };

  get requiresReload(): boolean {
    if (!this.searchParams) return false;

    if (
      this.searchParams.startDate.getTime() !== this.startDate.date?.getTime()
    ) {
      return true;
    }
    if (this.searchParams.endDate.getTime() !== this.endDate.date?.getTime()) {
      return true;
    }
    if (
      this.searchParams.selectedVillages.map((v) => v.value).join(",") !==
      this.selectedVillages.map((v) => v.value).join(",")
    ) {
      return true;
    }

    if (this.selectedTab === "Events") {
      return (
        this.eventSearchParams?.selectedCalendarType !==
        this.selectedCalendarType
      );
    }

    return false;
  }

  get canExport() {
    return this.selectedTab === "Events"
      ? this.events.length > 0
      : this.notices.length > 0;
  }

  get computedMetrics(): MetricsRow[] {
    return this.selectedTab === "Events"
      ? this.computedEventMetrics
      : this.computedNoticeMetrics;
  }

  get computedEventMetrics(): EventsMetricsRow[] {
    if (!this.eventSearchParams) return [];

    return summariseEventsIntoMetrics(
      this.events,
      this.eventSearchParams.selectedCalendarType,
      this.eventSearchParams.selectedVillages.map((v) => v.value),
      this.getVillages() ?? []
    );
  }

  get eventMetricsColumns(): EventMetricsColumn[] {
    return this.eventSearchParams?.selectedCalendarType === "Engage"
      ? EngageMetricsTypes
      : EnhancedMetricsTypes;
  }

  get computedNoticeMetrics(): NoticeMetricsRow[] {
    if (!this.noticeSearchParams) return [];

    return summariseNoticesIntoMetrics(
      this.notices,
      this.noticeSearchParams.selectedVillages.map((v) => v.value),
      this.getVillages() ?? []
    );
  }

  get noticeMetricsColumns(): NoticeMetricsColumn[] {
    return NoticeMetricsTypes;
  }

  get isSearchValid(): boolean {
    if (this.isDirty) {
      return (
        this.startDate.date !== null &&
        this.endDate.date !== null &&
        isDateOneAfterDateTwo(this.endDate.date, this.startDate.date) &&
        this.selectedVillages.length > 0
      );
    }
    return true;
  }

  setSelectedTab = (tab: MetricsTabOption) => {
    this.selectedTab = tab;
  };

  setSelectedVillages = (villages: KeyValue[]) => {
    this.selectedVillages = villages;
  };

  setSelectedCalendarType = (
    calendarType: CalendarTypeOptions | null | undefined
  ) => {
    this.selectedCalendarType = calendarType ?? "Enhanced";
  };

  getVillageMap = () => {
    return AuthUser.getInstance().villagesByCountry;
  };

  getVillages = () => {
    return AuthUser.getInstance().Villages;
  };

  private getSelectedPersonTypeIds(
    selectedVillageOptions: KeyValue[],
    calendarType: CalendarTypeOptions
  ): string {
    // A filter to be applied when iterating each village calendar on the selected villages
    const filterVillageCalendarForPersonType = (vc: VillageCalendar) => {
      switch (calendarType) {
        case "All":
          return true;
        case "Engage":
          return vc.calendarType === "Engage";
        case "Enhanced":
          return vc.calendarType === "Enhanced";
      }
    };

    const selectedVillageIds = selectedVillageOptions.map((v) => v.value);
    // Get all selected villages
    const selectedVillages =
      this.getVillages()?.filter((v) => selectedVillageIds.includes(v.id)) ??
      [];

    const cmsCalendars =
      this.villageCalendarsDomainStore?.villageCalendars ?? [];

    const personTypeIdsSet = new Set<string>();

    for (const village of selectedVillages) {
      const matchingCalendars =
        village.calendars?.filter(filterVillageCalendarForPersonType) ?? [];
      for (const calendar of matchingCalendars) {
        // 1. Try CMS match first (from villageCalendarsDomainStore). Match by
        // village + name like the events stores do (the CMS config id and the
        // Integration API calendar id are not guaranteed to share an id
        // space); a matching id is accepted as an alternative.
        const cmsMatch = cmsCalendars.find(
          (c) =>
            String(c.villageId) === String(village.id) &&
            (c.name === calendar.name ||
              (c.id != null &&
                calendar.id != null &&
                String(c.id) === String(calendar.id)))
        );
        const cmsIds = cmsMatch?.personTypeIds?.filter(
          (id): id is string => !!id && id !== "0"
        );

        if (cmsIds && cmsIds.length > 0) {
          cmsIds.forEach((id) => personTypeIdsSet.add(id));
          continue;
        }

        // 2. Try personTypeIds on the calendar itself
        const ownIds = calendar.personTypeIds?.filter(
          (id): id is string => !!id && id !== "0"
        );
        if (ownIds && ownIds.length > 0) {
          ownIds.forEach((id) => personTypeIdsSet.add(id));
          continue;
        }

        // 3. Fall back to calendar.personType. "0" is kept deliberately: an
        // empty personTypeIds filter means "all person types" to the Event
        // API, so dropping an unresolved combined calendar's "0" would
        // over-count instead of under-count.
        if (calendar.personType) {
          personTypeIdsSet.add(String(calendar.personType));
        }
      }
    }

    return Array.from(personTypeIdsSet).join(",");
  }
}
